import { useCallback, useEffect, useRef, useState } from 'react';
import { callBackend } from '../../../utils/api';
import { normalizeAumValue } from '../../../utils/aum';
import { AUM_UPDATED_EVENT } from '../dashboardUtils';
import {
    POSITION_SIZING_ATTRIBUTE_RECALCULATION,
    positionSizingAttributeDefaults,
} from '../../../utils/positionSizingAttributeContract';
import {
    changedTargetDefaults,
    normalizePosSizingStartingValues,
    pickStartingValues,
    readTargetVolDollarSuggestion,
    sameFraction,
} from '../../../utils/posSizingStartingValues';

/**
 * A member's Position Sizing starting values and the first-draft prompt.
 *
 * The values arrive through the existing risk-config load step, which calls
 * `applyStartingValuesResponse`. Until then `startingValues` is null and no
 * Position Sizing request that carries them may run. A member who has not
 * chosen is asked once, just before their first draft: from the Watchlist-only
 * start (shown on the Position Sizing page) or from the first Actual sync,
 * which waits for the choice.
 */
export default function usePosSizingStartingValues({
    userUid,
    activeView,
    setMaxPosSize,
    setNormalVol,
    setAum,
}) {
    const [state, setState] = useState(null);
    const [prompt, setPrompt] = useState(null);
    const [watchlistStartPendingUid, setWatchlistStartPendingUid] = useState(null);
    const [aumSetup, setAumSetup] = useState(null);
    const aumRequestRef = useRef(null);
    const stateRef = useRef(null);
    const userUidRef = useRef(userUid);
    const choiceWaiterRef = useRef(null);
    // Set by the Dashboard to the risk-config load step once it exists.
    const loadStartingValuesRef = useRef(null);
    userUidRef.current = userUid;

    const readCurrent = useCallback(() => (
        stateRef.current?.userUid === userUidRef.current ? stateRef.current : null
    ), []);

    const applyStartingValuesResponse = useCallback((response, responseUserUid) => {
        const normalized = normalizePosSizingStartingValues(response);
        if (!normalized) {
            throw new Error('Position Sizing starting values are invalid.');
        }
        const next = { ...normalized, userUid: responseUserUid };
        if (responseUserUid === userUidRef.current) {
            stateRef.current = next;
            setState(next);
        }
        return {
            startingValues: next,
            draftExists: response?.pos_sizing_draft_exists === true,
        };
    }, []);

    const saveStartingValues = useCallback(async (values) => {
        const current = readCurrent();
        if (!current) {
            throw new Error('Position Sizing starting values are still loading.');
        }
        const response = await callBackend('save_pos_sizing_starting_values', 'POST', {
            ...pickStartingValues(values),
            expected_version: current.version,
            ...(values.aum === undefined ? {} : { aum: values.aum }),
        });
        if (current.userUid !== userUidRef.current) {
            throw new Error('Your account changed. Reopen portfolio setup to continue.');
        }
        if (values.aum !== undefined) {
            const savedAum = normalizeAumValue(response?.aum);
            if (savedAum == null || savedAum <= 0) {
                throw new Error('Your saved AUM could not be verified. Retry to continue.');
            }
            setAum(savedAum);
            window.dispatchEvent(new CustomEvent(AUM_UPDATED_EVENT, { detail: { aum: savedAum } }));
        }
        return applyStartingValuesResponse(response, current.userUid).startingValues;
    }, [applyStartingValuesResponse, readCurrent, setAum]);

    const suggestTargetVolDollar = useCallback(async (source, values) => {
        const basis = pickStartingValues(values);
        const response = await callBackend('suggest_pos_sizing_target_vol_dollar', 'POST', {
            source,
            max_pos_size: basis.max_pos_size,
            normal_vol: basis.normal_vol,
            target_vol_pct: basis.target_vol_pct,
            scaler: basis.scaler,
        });
        return readTargetVolDollarSuggestion(response, basis);
    }, []);

    const applyTargetDefaultsToAll = useCallback(async (changes) => (
        callBackend('apply_pos_sizing_target_defaults_to_all', 'POST', changes)
    ), []);

    /**
     * Save from the Sizing defaults dialog: store the five values, apply a
     * changed Max Pos Size / Normal Vol through the toolbar's save (trade
     * ideas held), then write changed category fields to every Sizing Targets
     * row and run the recalculation a Sizing Targets edit runs (GMVs held).
     */
    const saveSizingDefaults = useCallback(async (values, {
        currentMaxPosSize,
        currentNormalVol,
        applySettings,
        hasDraft,
        reloadDraft,
    }) => {
        const previous = readCurrent()?.current;
        await saveStartingValues(values);
        if (
            !sameFraction(values.max_pos_size, currentMaxPosSize)
            || !sameFraction(values.normal_vol, currentNormalVol)
        ) {
            const applied = await applySettings({
                maxPosSize: values.max_pos_size,
                normalVol: values.normal_vol,
            });
            if (!applied) {
                throw new Error(
                    'Your defaults were saved, but Max Pos Size and Normal Vol were not applied. Finish any table edits, then save again.',
                );
            }
        }
        const changes = changedTargetDefaults(previous, values);
        if (Object.keys(changes).length === 0) return;
        await applyTargetDefaultsToAll(changes);
        if (!hasDraft()) return;
        try {
            await callBackend('recalculate_position_sizing_attribute_target_v2', 'POST', {
                contract: POSITION_SIZING_ATTRIBUTE_RECALCULATION,
                ...positionSizingAttributeDefaults({
                    defaultTargetVolPct: values.target_vol_pct,
                    defaultTargetVolDollar: values.target_vol_dollar,
                    defaultScaler: values.scaler,
                }),
            });
            await reloadDraft();
        } catch (error) {
            console.error('Error recalculating Position Sizing after new defaults:', error);
            throw new Error(
                'Your defaults were saved and applied to every category, but the draft could not be recalculated. Use Sync Draft to update it.',
            );
        }
    }, [applyTargetDefaultsToAll, readCurrent, saveStartingValues]);

    // Save the prompt's values and give every stored category the chosen
    // targets: before a first draft those rows hold only the values seeded at
    // Watchlist upload, which Actual trade ideas read. The prompt stays open
    // until both succeed. Without a draft the toolbar settings are the
    // starting values, so they follow the choice.
    const choosePromptValues = useCallback(async (values) => {
        const choiceUserUid = userUidRef.current;
        if (!Number.isFinite(values.aum) || values.aum <= 0) {
            throw new Error('Enter assets under management above 0.');
        }
        let saved;
        let chosenHere = true;
        try {
            saved = await saveStartingValues(values);
        } catch (error) {
            if (choiceUserUid !== userUidRef.current) throw error;
            // Chosen meanwhile in another window: continue with that choice.
            const fresh = error?.status === 409
                ? await loadStartingValuesRef.current?.()
                : null;
            if (!fresh?.startingValues?.chosen) throw error;
            const response = await callBackend('fetch_aum', 'POST', {});
            const savedAum = normalizeAumValue(response?.aum);
            if (savedAum == null || savedAum <= 0) throw error;
            if (choiceUserUid !== userUidRef.current) throw error;
            setAum(savedAum);
            window.dispatchEvent(new CustomEvent(AUM_UPDATED_EVENT, { detail: { aum: savedAum } }));
            saved = fresh.startingValues;
            chosenHere = false;
        }
        if (chosenHere) {
            try {
                await applyTargetDefaultsToAll({
                    target_vol_pct: saved.current.target_vol_pct,
                    target_vol_dollar: saved.current.target_vol_dollar,
                    scaler: saved.current.scaler,
                });
            } catch (error) {
                console.error('Error applying starting values to categories:', error);
                throw new Error(
                    'Your choice could not be applied to every category. Try again.',
                );
            }
        }
        if (choiceUserUid !== userUidRef.current) {
            throw new Error('Your account changed. Reopen portfolio setup to continue.');
        }
        setMaxPosSize(saved.current.max_pos_size);
        setNormalVol(saved.current.normal_vol);
        setPrompt(null);
        setWatchlistStartPendingUid(null);
        const waiter = choiceWaiterRef.current;
        choiceWaiterRef.current = null;
        waiter?.resolve(saved.current);
        return saved;
    }, [applyTargetDefaultsToAll, saveStartingValues, setAum, setMaxPosSize, setNormalVol]);

    const requestFirstDraftChoice = useCallback(async (defaults) => {
        if (!choiceWaiterRef.current) {
            let resolve;
            const promise = new Promise((done) => { resolve = done; });
            choiceWaiterRef.current = { promise, resolve };
            const promptUserUid = userUidRef.current;
            let suggestion = null;
            try {
                suggestion = await suggestTargetVolDollar('actual', defaults);
            } catch (error) {
                // The prompt still opens with the unchanged starting values.
                console.error('Target Vol ($) suggestion is unavailable:', error);
            }
            setPrompt({
                userUid: promptUserUid,
                picked: suggestion
                    ? { ...defaults, target_vol_dollar: suggestion.targetVolDollar }
                    : { ...defaults },
                suggestion,
            });
        }
        return choiceWaiterRef.current.promise;
    }, [suggestTargetVolDollar]);

    /**
     * The values an Actual sync publishes with. Before a first draft for a
     * member who has not chosen, this waits for the prompt and returns the
     * choice. `firstDraft` says the publication creates the draft, so its Max
     * Pos Size and Normal Vol are the starting values too.
     */
    const resolveActualSyncStartingValues = useCallback(async () => {
        const loaded = readCurrent();
        if (loaded?.chosen) {
            return { values: { ...loaded.current }, firstDraft: false };
        }
        // Read the current state: it also says whether a draft exists.
        const fresh = await loadStartingValuesRef.current?.();
        if (!fresh?.startingValues) {
            throw new Error('Position Sizing starting values could not be loaded.');
        }
        if (fresh.startingValues.chosen || fresh.draftExists) {
            return {
                values: { ...fresh.startingValues.current },
                firstDraft: !fresh.draftExists,
            };
        }
        const chosen = await requestFirstDraftChoice(fresh.startingValues.current);
        return { values: { ...chosen }, firstDraft: true };
    }, [readCurrent, requestFirstDraftChoice]);

    // The Watchlist-only start shows an empty table; ask before the first draft.
    const markWatchlistStartPromptPending = useCallback(() => {
        if (readCurrent()?.chosen === false) {
            setWatchlistStartPendingUid(userUidRef.current);
        }
    }, [readCurrent]);

    const startingValues = state?.userUid === userUid ? state : null;
    // A prompt is opened only for a member who has not chosen, and stays open
    // until its choice completes (saving marks the values chosen first).
    let onboardingPrompt = null;
    if (startingValues) {
        if (prompt?.userUid === userUid) {
            onboardingPrompt = {
                source: 'actual',
                picked: prompt.picked,
                suggestion: prompt.suggestion,
            };
        } else if (
            watchlistStartPendingUid === userUid
            && activeView === 'pos-sizing'
        ) {
            // No holdings to calibrate against: the user types trade ideas.
            onboardingPrompt = {
                source: 'watchlist_start',
                picked: { ...startingValues.current },
                suggestion: null,
            };
        }
    }

    const aumPromptKey = onboardingPrompt ? `${userUid}:${onboardingPrompt.source}` : null;
    const loadOnboardingAum = useCallback(async () => {
        if (!aumPromptKey) return;
        const request = { userUid, key: aumPromptKey };
        aumRequestRef.current = request;
        setAumSetup({ key: aumPromptKey, loading: true });
        try {
            const response = await callBackend('fetch_aum', 'POST', {});
            const current = normalizeAumValue(response?.aum);
            const fallback = normalizeAumValue(response?.default_aum);
            if (fallback == null || fallback <= 0 || (response?.aum != null && (current == null || current <= 0))) {
                throw new Error('Assets under management could not be loaded. Retry to continue.');
            }
            if (aumRequestRef.current === request && request.userUid === userUidRef.current) {
                setAumSetup({ key: aumPromptKey, loading: false, aum: current ?? fallback, stored: current != null });
            }
        } catch (error) {
            if (aumRequestRef.current === request && request.userUid === userUidRef.current) {
                setAumSetup({ key: aumPromptKey, loading: false, error: 'Assets under management could not be loaded. Retry to continue.' });
            }
        }
    }, [aumPromptKey, userUid]);
    useEffect(() => {
        if (aumPromptKey && aumSetup?.key !== aumPromptKey) void loadOnboardingAum();
    }, [aumPromptKey, aumSetup?.key, loadOnboardingAum]);
    if (onboardingPrompt) {
        const currentAum = aumSetup?.key === aumPromptKey ? aumSetup : null;
        onboardingPrompt = { ...onboardingPrompt, aum: currentAum?.aum ?? null,
            aumStored: currentAum?.stored === true, aumLoading: !currentAum || currentAum.loading,
            aumError: currentAum?.error || '' };
    }

    return {
        startingValues,
        onboardingPrompt,
        loadStartingValuesRef,
        applyStartingValuesResponse,
        saveSizingDefaults,
        suggestTargetVolDollar,
        choosePromptValues,
        retryOnboardingAum: loadOnboardingAum,
        resolveActualSyncStartingValues,
        markWatchlistStartPromptPending,
    };
}
